/**
 * Platform Center 相关类型定义
 * API控制台接口相关的TypeScript类型
 */

// ============ API项目用量相关类型 ============

/**
 * API项目用量请求参数
 */
export interface ApiOperateRecordListReq {
  /** 数据维度:1.成功任务数，2.生成图片数，3.并发数 */
  dimension?: number;
  /** 结束时间:yyyy-MM-dd */
  endDate?: string;
  /** 时间粒度：1.小时，2.天 */
  granularity?: number;
  /** 第几页 */
  pageNo?: number;
  /** 每页多少条 */
  pageSize?: number;
  /** 开始时间:yyyy-MM-dd */
  startDate?: string;
  /** tokenIds */
  tokenIdList?: number[];
}

/**
 * API项目用量明细响应
 */
export interface ApiOperateRecordDetailResp {
  /** 时间 */
  groupTime: string;
  /** 生成图片数 */
  imgCount: number;
  /** 并发数 */
  qpsCount: number;
  /** 任务成功数 */
  success: number;
}

/**
 * API项目用量分析请求参数
 */
export interface ApiOperateRecordAnalyseReq {
  /** 结束时间:yyyy-MM-dd */
  endDate?: string;
  /** 时间粒度：1.小时，2.天 */
  granularity?: number;
  /** 第几页 */
  pageNo?: number;
  /** 每页多少条 */
  pageSize?: number;
  /** 开始时间:yyyy-MM-dd */
  startDate?: string;
  /** tokenIds */
  tokenIdList?: number[];
}

/**
 * API项目用量分析响应
 */
export interface ApiOperateRecordAnalyseResp {
  /** 时间 */
  groupTime: string;
  /** 生成图片数 */
  imgCount: number;
  /** 并发数 */
  qpsCount: number;
  /** 任务成功数 */
  success: number;
}

/**
 * API项目用量统计请求参数
 */
export interface ApiOperateRecordStatisticReq {
  /** 结束时间:yyyy-MM-dd */
  endDate?: string;
  /** 开始时间:yyyy-MM-dd */
  startDate?: string;
  /** tokenIds */
  tokenIdList?: number[];
}

/**
 * API项目用量统计响应
 */
export interface ApiOperateRecordStatisticResp {
  /** 生成图片数 */
  imgCount: number;
  /** 并发数 */
  qpsCount: number;
  /** 任务成功数 */
  success: number;
}

/**
 * API项目用量多维度分析图请求参数
 */
export interface ApiOperateRecordAnalyticalDiagramReq {
  /** 结束时间:yyyy-MM-dd */
  endDate?: string;
  /** 时间粒度：1.小时，2.天 */
  granularity?: number;
  /** 开始时间:yyyy-MM-dd */
  startDate?: string;
  /** tokenIds */
  tokenIdList?: number[];
}

/**
 * API项目用量多维度分析图响应
 */
export interface ApiOperateRecordAnalyticalDiagramResp {
  /** token id */
  id: number;
  /** token名称 */
  name: string;
  /** 该token的时间序列数据列表 */
  list: ApiOperateRecordAnalyseResp[];
}

// ============ API项目资源包相关类型 ============

/**
 * API项目资源包明细请求参数
 */
export interface ApiPointsDetailPageReq {
  /** 第几页 */
  pageNo?: number;
  /** 每页多少条 */
  pageSize?: number;
  /** 资源包名称/ID */
  searchKey?: string;
  /** 生效状态(0.待生效，1.生效中，2.已失效) */
  status?: number;
}

/**
 * API项目资源包明细响应
 */
export interface ApiPointsDetailPageResp {
  /** 可用积分 */
  availablePoints: number;
  /** 生效时间 */
  ctime: string;
  /** 失效时间 */
  ddl: string;
  /** 资源包ID */
  id: number;
  /** 资源包名称 */
  name: string;
  /** 并发数 */
  qps: number;
  /** 生效状态(0.待生效，1.生效中，2.已失效) */
  status: number;
  /** 总积分 */
  totalPoints: number;
}

/**
 * API项目资源包统计响应
 */
export interface ApiPointsStatisticsResp {
  /** 可用积分 */
  availablePoints: number;
  /** 当前并发数 */
  currentQps: number;
  /** 生效中资源包 */
  effectPointCount: number;
  /** 已购资源包 */
  purchasesPointCount: number;
  /** 总积分 */
  totalPoints: number;
}

// ============ API密钥相关类型 ============

/**
 * API密钥响应
 */
export interface ApiUserTokenResp {
  /** ak */
  ak: string;
  /** apiId */
  apiId: number;
  /** 创建时间 */
  ctime: string;
  /** id */
  id: number;
  /** 项目名称 */
  name: string;
  /** sk */
  sk: string;
  /** 启用状态(1.启用，0.未启用) */
  status: number;
}

/**
 * 添加API密钥请求参数
 */
export interface ApiUserTokenAddReq {
  /** 项目名称 */
  name: string;
}

/**
 * 编辑API密钥请求参数
 */
export interface ApiUserTokenEditReq {
  /** id */
  id: number;
  /** 项目名称 */
  name: string;
}

/**
 * API密钥状态请求参数
 */
export interface ApiUserTokenStatusReq {
  /** id */
  id: number;
  /** 启用状态：1.启用，0.未启用 */
  status: number;
}

// ============ 套餐相关类型 ============

/**
 * 套餐列表请求参数
 */
export interface ApiMenuListReq {
  userId?: string;
}

/**
 * 套餐响应
 */
export interface ApiMenuResp {
  /** 折扣 */
  discount: number;
  /** 到期天数 */
  expireDays: number;
  /** 到期类型（1.日，2.月，3.年） */
  expireType: number;
  /** 名称 */
  name: string;
  /** 积分 */
  points: number;
  /** 价格 */
  price: number;
  /** 商品id */
  productId: string;
  /** 购买限制 (0.不限制，1.仅能购买一次) */
  purchase: number;
  /** 并发数 */
  qps: number;
  /** 购买状态 (1.可购买，2.不可购买) */
  status: number;
  /** 类型 (1.资源包,2.套餐,3.并发包) */
  type: number;
  /** 积分类型 */
  pointsType: string;
  /** 订单类型 */
  orderType: string;
  /** 商品id */
  commodityId: string;
  /** 并发任务资源包日单价 */
  qpsDayPrice: number;
  /** 并发任务资源包月单价 */
  qpsMonthPrice: number;
}

// ============ 枚举类型 ============

/**
 * 数据维度枚举
 */
export enum DataDimension {
  /** 成功任务数 */
  SUCCESS_TASKS = 1,
  /** 生成图片数 */
  GENERATED_IMAGES = 2,
  /** 并发数 */
  CONCURRENCY = 3
}

/**
 * 时间粒度枚举
 */
export enum TimeGranularity {
  /** 小时 */
  HOUR = 1,
  /** 天 */
  DAY = 2
}

/**
 * 资源包状态枚举
 */
export enum ResourcePackageStatus {
  /** 待生效 */
  PENDING = 0,
  /** 生效中 */
  ACTIVE = 1,
  /** 已失效 */
  EXPIRED = 2
}

/**
 * API密钥状态枚举
 */
export enum ApiTokenStatus {
  /** 未启用 */
  DISABLED = 0,
  /** 启用 */
  ENABLED = 1
}

/**
 * 套餐类型枚举
 */
export enum PackageType {
  /** 资源包 */
  RESOURCE_PACKAGE = 1,
  /** 套餐 */
  PLAN = 2,
  /** 并发包 */
  CONCURRENCY_PACKAGE = 3
}

export enum OrderType {
  /** 试用资源包 */
  API_RESOURCE_PACKAGE = "api_resource_package",
  /** 标准版套餐 */
  API_STANDARD_PACKAGE = "api_standard_package",
  /** QPS并发包 */
  API_QPS_PACKAGE = "api_qps_package"
}

/**
 * 到期类型枚举
 */
export enum ExpireType {
  /** 日 */
  DAYS = 1,
  /** 月 */
  MONTHS = 2,
  /** 年 */
  YEARS = 3
}

/**
 * 购买限制枚举
 */
export enum PurchaseLimit {
  /** 不限制 */
  UNLIMITED = 0,
  /** 仅能购买一次 */
  ONCE_ONLY = 1
}

/**
 * 购买状态枚举
 */
export enum PurchaseStatus {
  /** 可购买 */
  AVAILABLE = 1,
  /** 不可购买 */
  UNAVAILABLE = 2
}

// ============ 对公支付订单相关类型 ============

/**
 * API对公支付订单响应
 */
export interface ApiExistCorporateOrderResp {
  /** 商品id */
  commodityId: string;
  /** 对公订单标识 */
  corporateId: string;
  /** 对公订单公司全称 */
  corporateName: string;
  /** 对公订单公司电话 */
  corporatePhone: string;
  /** 对公订单回执地址 */
  corporateUrl: string;
  /** 下单时间（date-time格式，如：2025-10-14T12:00:00） */
  ctime: string;
  /** 有效天数 */
  durationDay: number;
  /** 结束时间（date-time格式，如：2025-10-14T12:00:00） */
  endTime: string;
  /** 首购金额 */
  firstPrice: number;
  /** 人员数量 */
  headCount: number;
  /** 订单号 */
  innerOrderId: string;
  /** 折扣系数 */
  levelRate: number;
  /** 会员类型：pro、plus */
  memberType: string;
  /** 订单状态（具体状态值需参考业务文档，如：pending、paid、completed等） */
  orderStatus: string;
  /** 订单类型：member（会员订单）、points（积分订单） */
  orderType: string;
  /** 对公支付我方收款主体账户（仅对公支付商品返回） */
  ourReceiveAccount: string;
  /** 对公支付我方收款主体开户行（仅对公支付商品返回） */
  ourReceiveBank: string;
  /** 我方收款主体名称（仅对公支付商品返回） */
  ourReceiveSubjectName: string;
  /** 支付渠道：alipay（支付宝）、wechat（微信支付）、corporate（对公支付） */
  payChannel: string;
  /** 支付时间（date-time格式，如：2025-10-14T12:00:00；未支付时可能为空） */
  payTime: string;
  /** 开始时间（date-time格式，如：2025-10-14T12:00:00） */
  startTime: string;
  /** 订单名称 */
  subject: string;
  /** 团队名称 */
  teamName: string;
  /** 总金额：单位为分（如100表示1元） */
  totalAmount: number;
  /** 支付流水号 */
  transactionId: string;
  /** 续购金额 */
  unitPrice: number;
}